import { useTranslation } from 'react-i18next';

import type { EntryCardHeaderProps } from '@/components/entry-card';
import { ds } from '@/components/lib/design.system';
import { Bet, BetEvent } from '@/feature/bets-sbk/hooks/types';
import { useGameStatus, useMarketStatus } from '@/feature/bets-sbk/hooks/use-bet-card';
import { SbkBetVariant } from '@/feature/bets-sbk/utils/classify-bet-variant';
import { useMarketName } from '@/feature/event-details-sbk/hooks/use-market-name';
import { useOptionName } from '@/feature/event-details-sbk/hooks/use-option-name';
import { useFormatOdds } from '@/hooks/use-format-odds';
import { useOddsFormat } from '@/hooks/use-odds-format';
import { formatMatchupString } from '@/utils/sport-utils';

import { getAdjustedOdds } from './utils';

/** Title for a single bet — the selected option's display name (e.g. "New York Rangers"). */
const useSingleBetTitle = (bet: Bet) => {
    const selection = bet.events[0].selections[0];
    const sportName = bet.events[0].sport.name;
    return useOptionName({
        optionType: selection.option.option_type,
        marketType: selection.market.market_type,
        fallback: selection.option.description,
        homeTeam: bet.events[0].home_team,
        awayTeam: bet.events[0].away_team,
        sportName,
        isDisplayFullName: true,
    });
};

/** Subtitle for a single bet — "{market} • {matchup} • {game status}". */
const useSingleBetSubtitle = (bet: Bet) => {
    const event = bet.events[0];
    const market = event.selections[0].market;
    const sportName = event.sport.name;
    const marketName = useMarketName({
        sportName,
        event,
        fallback: market.description,
        marketType: market.market_type,
        player: market.player,
        appendPlayerName: true,
        appendMicroMarketDescription: market.is_micro_market,
        isExtendedName: true,
    });
    const marketStatus = useMarketStatus(event);
    const gameStatus = useGameStatus(bet);

    return [marketName, marketStatus, gameStatus].join(' • ');
};

const formatEventTeams = (event: Pick<BetEvent, 'home_team' | 'away_team' | 'sport'>) => {
    return formatMatchupString(event.home_team?.short_code, event.away_team?.short_code, event.sport?.name ?? '');
};

/** Subtitle for a multi-event bet — the matchups joined (e.g. "PHI @ NYM • LAD @ NYY"). */
const getComboBetSubtitle = (events: Pick<BetEvent, 'home_team' | 'away_team' | 'sport'>[]) => {
    return events.map(event => formatEventTeams(event)).join(' • ');
};

/**
 * Header (title, subtitle, trailing odds) for a sportsbook entry card, selected by its display
 * variant. Shared by the open and settled cards — the settled card layers its status badge on top.
 */
export const useSbkEntryHeader = (bet: Bet, variant: SbkBetVariant): EntryCardHeaderProps => {
    const { t } = useTranslation('bets');
    const { formatOdds } = useFormatOdds();
    const oddsFormat = useOddsFormat();

    const singleTitle = useSingleBetTitle(bet);
    const singleSubtitle = useSingleBetSubtitle(bet);
    const matchups = getComboBetSubtitle(bet.events);
    const gameStatus = useGameStatus(bet);

    // Betr Bucks bets display the bonus-adjusted odds, not the raw requested odds.
    const trailing = formatOdds(getAdjustedOdds(bet, oddsFormat));
    const trailingColor = ds.colorToken.text.brand;
    const titleColor = ds.colorToken.text.primary;
    const count = { count: bet.selection_count };

    // SGP is single-event, so it pairs the matchup with the game time; parlay/SGP+ list every matchup.
    switch (variant) {
        case 'SINGLE':
            return { title: singleTitle, subtitle: singleSubtitle, trailing, trailingColor, titleColor };
        case 'PARLAY':
            return { title: t('parlay_bet_name', count), subtitle: matchups, trailing, trailingColor, titleColor };
        case 'SGP':
            return {
                title: t('sgp_bet_name', count),
                subtitle: [matchups, gameStatus].join(' • '),
                trailing,
                trailingColor,
                titleColor,
            };
        case 'SGP+':
            return { title: t('sgp_plus_bet_name', count), subtitle: matchups, trailing, trailingColor, titleColor };
    }
};
